import { createRouter, createWebHistory } from 'vue-router';

import { useAccountStore } from '@/stores/account';

// Every route is lazy-loaded — each view becomes its own chunk. A visitor on
// /c/:slug downloads only the shared core (Vue + Router + Pinia) plus the chat
// chunk, never the dashboard; a dashboard user never downloads the chat.
export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', name: 'home', component: () => import('@/views/HomeView.vue') },
    {
      // Public visitor chat opened by a scanned QR — no auth.
      path: '/c/:slug',
      name: 'chat',
      component: () => import('@/views/chat/ChatView.vue'),
    },
    {
      // « Mot de passe oublié » — asks for an email, no auth.
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/auth/ForgotPasswordView.vue'),
    },
    {
      // Reached from the emailed link (?token=…) — sets a new password, no auth.
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/auth/ResetPasswordView.vue'),
    },
    {
      // Conversation-first onboarding with Dimmitri — first stop after register.
      path: '/onboarding',
      name: 'onboarding',
      component: () => import('@/views/OnboardingView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/dashboard/DashboardView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/conversations',
      name: 'conversations',
      component: () => import('@/views/dashboard/ConversationsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/conversations/:id',
      name: 'conversation-detail',
      component: () => import('@/views/dashboard/ConversationDetailView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/assistants',
      name: 'assistants',
      component: () => import('@/views/dashboard/AssistantsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/assistants/:id',
      name: 'assistant-detail',
      component: () => import('@/views/dashboard/AssistantDetailView.vue'),
      meta: { requiresAuth: true },
    },
    {
      // « Colles » of one assistant — questions the AI couldn't answer.
      path: '/assistants/:id/unanswered',
      name: 'assistant-unanswered',
      component: () => import('@/views/dashboard/UnansweredView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/supports',
      name: 'supports',
      component: () => import('@/views/dashboard/SupportsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/dashboard/SettingsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      // Admin supervision — a separate page, outside the 5-tab nav (CLAUDE.md
      // §6/§15). Reachable only by the hard-coded administrator account.
      path: '/admin',
      name: 'admin',
      component: () => import('@/views/admin/AdminView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
  ],
});

let hydrated = false;

router.beforeEach(async (to) => {
  // Public, no-auth routes need no account state — return early so a visitor
  // (e.g. arriving from a password-reset email) never triggers an /api/me probe
  // (it would only 401). `hydrated` stays false so a later in-app navigation
  // still hydrates the session.
  if (to.name === 'chat' || to.name === 'forgot-password' || to.name === 'reset-password') {
    return true;
  }

  const account = useAccountStore();

  if (!hydrated) {
    hydrated = true;
    await account.refresh();
  }

  const isAuthed = account.account !== null;

  if (to.meta.requiresAuth && !isAuthed) {
    return { name: 'home' };
  }

  // The admin page is gated on the account being the hard-coded administrator
  // — a regular prestataire (or an impersonation tab) is sent to its dashboard.
  if (to.meta.requiresAdmin && !account.account?.isAdmin) {
    return { name: 'dashboard' };
  }

  if (to.name === 'home' && isAuthed) {
    return { name: 'dashboard' };
  }

  return true;
});
